import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NumberFlowReact, { useCanAnimate } from '@number-flow/react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

export interface NumberFlowProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: {
    /** Pre-formatted string: "$1,234.32", "90.12%", "1,234", "123" */
    text?: string;
    /** Raw numeric value */
    value?: number | string;
    /** ISO 4217 currency code, e.g. "USD", "EUR" */
    currency?: string;
    /** BCP 47 language tag, e.g. "en-US". Defaults to i18n.language. */
    language?: string;
  };
}

type ParsedText = {
  value: number;
  currency?: string;
  locales: string;
  style: 'currency' | 'percent' | 'decimal';
};

type ResolvedProps = {
  value: number;
  format: Intl.NumberFormatOptions;
  locales: Intl.LocalesArgument;
};

function parseText(text: string): ParsedText {
  const trimmed = text.trim();

  if (trimmed.startsWith('$')) {
    const numeric = trimmed.replace(/^\$/, '').replace(/,/g, '');

    return {
      value: parseFloat(numeric) || 0,
      currency: 'USD',
      locales: 'en-US',
      style: 'currency',
    };
  }

  if (trimmed.endsWith('%')) {
    const numeric = trimmed.slice(0, -1).replace(/,/g, '');

    return {
      value: parseFloat(numeric) || 0,
      locales: 'en-US',
      style: 'percent',
    };
  }

  return {
    value: parseFloat(trimmed.replace(/,/g, '')) || 0,
    locales: 'en-US',
    style: 'decimal',
  };
}

function minDecimals(value: number): 0 | 2 {
  return value % 1 === 0 ? 0 : 2;
}

function buildFormat(
  style: ParsedText['style'],
  currency?: string,
  minFractionDigits: 0 | 2 = 0,
): Intl.NumberFormatOptions {
  const base = { minimumFractionDigits: minFractionDigits, maximumFractionDigits: 2 };

  if (style === 'currency' && currency) {
    return {
      ...base,
      style: 'currency',
      currency,
    };
  }

  if (style === 'percent') {
    return {
      ...base,
      style: 'percent',
    };
  }

  return {
    ...base,
    style: 'decimal',
  };
}

function resolveNumberFlowProps(
  text: string | undefined,
  rawValue: number | string | undefined,
  currency: string | undefined,
  language: string | undefined,
  i18nLanguage: string,
): ResolvedProps {
  if (text) {
    const parsed = parseText(text);
    const value = parsed.style === 'percent' ? parsed.value / 100 : parsed.value;
    const safe = isNaN(value) ? 0 : value;

    return {
      value: safe,
      format: buildFormat(parsed.style, parsed.currency, minDecimals(safe)),
      locales: parsed.locales,
    };
  }

  const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue ?? 0;
  const safe = isNaN(value) ? 0 : value;

  return {
    value: safe,
    format: buildFormat(currency ? 'currency' : 'decimal', currency, minDecimals(safe)),
    locales: language ?? i18nLanguage ?? 'en-US',
  };
}

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

export const NumberFlow = (componentProps: NumberFlowProps) => {
  let props = componentProps;
  const dataElementContext = useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const { i18n } = useTranslation();
  const { text, value: rawValue, currency, language } = props.properties ?? {};

  const { value, format, locales } = useMemo(
    () => resolveNumberFlowProps(text, rawValue, currency, language, i18n.language),
    [text, rawValue, currency, language, i18n.language],
  );

  const canAnimate = useCanAnimate({ respectMotionPreference: true });

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <NumberFlowReact value={value} format={format} locales={locales} animated={canAnimate} />
    </ModuleElementDiv>
  );
};

export default NumberFlow;
